const normalizeLookupValue = (value) =>
	String(value || "")
		.trim()
		.toLowerCase();

export function getExerciseHistoryDisplayName(exercise) {
	return String(exercise?.exercise_name || exercise?.name || "").trim();
}

const isConditioningExercise = (exercise) =>
	normalizeLookupValue(exercise?.exercise_type) === "conditioning" ||
	normalizeLookupValue(exercise?.type) === "conditioning" ||
	Boolean(normalizeLookupValue(exercise?.conditioning_type));

/**
 * Movement names carried by a (possibly composite) exercise name.
 *
 * Mirrors `_movement_names` in backend/services/one_rm.py: complex/EMOM parents
 * carry an auto-generated "A + B + C" name, so each component must be matchable
 * on its own. Never substring-matches ("Clean" must not match "Power Clean").
 */
export function getExerciseHistoryMovementNames(rawName) {
	const names = new Set();
	const primary = normalizeLookupValue(rawName);
	if (!primary) return names;
	names.add(primary);
	if (String(rawName).includes(" + ")) {
		String(rawName)
			.split(" + ")
			.forEach((part) => {
				const component = normalizeLookupValue(part);
				if (component) names.add(component);
			});
	}
	return names;
}

export function getExerciseHistoryLookupCandidates(exercise) {
	if (!exercise) return [];

	const conditioningCandidates = [
		exercise.library_conditioning_id,
		exercise.conditioning_config?.library_conditioning_id,
		exercise.shared_exercise_id,
		exercise.conditioning_config?.shared_exercise_id,
		exercise.source_exercise_id,
		exercise.conditioning_config?.source_exercise_id,
		exercise.exercise_id,
		exercise.conditioning_config?.exercise_id,
		exercise._id,
	];

	// `exercise_id` first: it is the link the coach last chose in the builder, so
	// it is the only id guaranteed to describe THIS movement. `source_exercise_id`
	// survives week duplication and can point at the library exercise the row was
	// cloned from BEFORE it was edited into a different movement, so it is only a
	// fallback (and the caller must still validate the resolved name).
	const generalCandidates = [
		exercise.exercise_id,
		exercise.source_exercise_id,
		exercise.shared_exercise_id,
		exercise._id,
	];

	const orderedCandidates = isConditioningExercise(exercise)
		? conditioningCandidates
		: generalCandidates;

	return Array.from(
		new Set(
			orderedCandidates
				.map((candidate) => normalizeLookupValue(candidate))
				.filter(Boolean),
		),
	);
}

export function extractExerciseHistoryItems(payload) {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.items)) return payload.items;
	if (Array.isArray(payload?.entries)) return payload.entries;
	if (Array.isArray(payload?.results)) return payload.results;
	return [];
}

/**
 * Does a performance-profile history payload actually describe this exercise?
 *
 * The profile endpoint is keyed by id alone, so a stale `source_exercise_id`
 * resolves to a real — but WRONG — tracked lift and returns its history (a
 * "Deadlift" row cloned from a "Bench Press" row used to show bench numbers).
 * The backend already matches the underlying results by movement name, so the
 * config name is the authoritative cross-check.
 *
 * Conditioning is exempt: its displayed name is built from the template config
 * and legitimately differs from the stored template name.
 */
export function doesProfileHistoryConfigMatchExercise(payload, exercise) {
	if (!exercise) return false;
	if (isConditioningExercise(exercise)) return true;

	const configName = normalizeLookupValue(payload?.exercise_name);
	const exerciseName = getExerciseHistoryDisplayName(exercise);
	if (!configName || !exerciseName) return true;

	return getExerciseHistoryMovementNames(exerciseName).has(configName);
}

export function doesHistoryResultMatchExercise(result, exercise) {
	if (!result || !exercise) return false;

	const exerciseCandidates = new Set(
		getExerciseHistoryLookupCandidates(exercise),
	);
	const matchesById = () =>
		[
			result.exercise_id,
			result.shared_exercise_id,
			result.source_exercise_id,
			result.library_conditioning_id,
			result.conditioning_id,
			result.exercise?._id,
			result.exercise?.exercise_id,
			result.exercise?.shared_exercise_id,
			result.exercise?.source_exercise_id,
			result.conditioning_config?.library_conditioning_id,
			result.conditioning_config?.shared_exercise_id,
			result.conditioning_config?.source_exercise_id,
		]
			.map((candidate) => normalizeLookupValue(candidate))
			.filter(Boolean)
			.some((candidate) => exerciseCandidates.has(candidate));

	const exerciseNames = getExerciseHistoryMovementNames(
		getExerciseHistoryDisplayName(exercise),
	);
	const resultNames = new Set();
	[result.exercise_name, result.exercise?.exercise_name, result.exercise?.name]
		.filter(Boolean)
		.forEach((name) =>
			getExerciseHistoryMovementNames(name).forEach((n) => resultNames.add(n)),
		);

	if (
		!isConditioningExercise(exercise) &&
		exerciseNames.size &&
		resultNames.size
	) {
		// Both sides name the movement: the name wins over any id link, otherwise a
		// result logged under a stale library id lands in the wrong exercise.
		return Array.from(resultNames).some((name) => exerciseNames.has(name));
	}

	return matchesById();
}

const toFiniteNumber = (value) => {
	if (value === null || value === undefined || value === "") return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const firstNumericSchemaValue = (result) => {
	const values = Array.isArray(result?.schema_values)
		? result.schema_values
		: [];
	for (const entry of values) {
		const parsed = toFiniteNumber(entry?.value);
		if (parsed !== null) return parsed;
	}
	return null;
};

const sumIntervalScores = (result) => {
	const scores = Array.isArray(result?.interval_scores)
		? result.interval_scores
		: [];
	let total = 0;
	let found = false;
	for (const raw of scores) {
		const parsed = toFiniteNumber(raw);
		if (parsed !== null) {
			total += parsed;
			found = true;
		}
	}
	return found ? total : null;
};

/**
 * Builds a compact progression series for the exercise history chart.
 *
 * Works on BOTH shapes the history dialog is fed: performance-profile items
 * (`value`/`raw_weight`/`estimated_1rm`/`unit`) and raw workout_results
 * (`weight_kg`/`time_seconds`/`score`/`schema_values`). Returns null when there
 * are fewer than two plottable points — a single dot is not a progression.
 */
export function buildExerciseHistoryChartSeries(results, exercise) {
	const list = Array.isArray(results) ? results.filter(Boolean) : [];
	if (list.length < 2) return null;

	const exType = normalizeLookupValue(
		exercise?.exercise_type || exercise?.type || "",
	);
	const sample = list[0] || {};
	// Tiro. Va intercettato **prima** di `isTimed`: un risultato di tiro ha
	// `time_seconds` denormalizzato, quindi senza questo ramo il grafico
	// traccerebbe i secondi al posto dell'hit factor — che è la metrica su
	// cui quel drill si giudica.
	const isShooting =
		exType === "dry_fire" ||
		exType === "live_fire" ||
		!!sample?.shooting;
	const hasWeight =
		exType === "strength" ||
		sample?.unit === "kg" ||
		sample?.raw_weight !== undefined ||
		sample?.weight_kg !== undefined;
	const isTimed =
		!hasWeight &&
		(["running", "swim", "bike", "erg"].includes(exType) ||
			["seconds", "minutes", "minutes_seconds"].includes(sample?.unit) ||
			sample?.time_seconds !== undefined);

	let mode = "value";
	if (isShooting) mode = "shooting";
	else if (hasWeight) mode = "weight";
	else if (isTimed) mode = "time";

	const points = [];
	for (const result of list) {
		const rawDate = result?.date || result?.created_at || "";
		const dateLabel = rawDate ? String(rawDate).slice(0, 10) : "";
		let value = null;
		let estimated1rm = null;
		let reps = null;

		if (mode === "shooting") {
			// La metrica è **congelata** sul risultato insieme al punteggio: si
			// legge quella, o una tabella corretta in seguito riscriverebbe la
			// forma dello storico.
			const shooting = result?.shooting || {};
			if (shooting.prescription_format === "free") {
				value = toFiniteNumber(shooting.self_rating);
			} else if ((shooting.metric || "hit_factor") === "hit_factor") {
				value = toFiniteNumber(shooting.hit_factor);
			} else if (shooting.metric === "time_plus") {
				value =
					toFiniteNumber(shooting.total_time_seconds) ??
					toFiniteNumber(shooting.best_time_seconds);
			} else if (shooting.mode === "dry_fire") {
				value = toFiniteNumber(shooting.best_time_seconds);
			} else {
				value = toFiniteNumber(shooting.total_points);
			}
			// `score` è il valore denormalizzato dal backend: vale da rete di
			// sicurezza sui documenti che il ramo sopra non copre.
			if (value === null) value = toFiniteNumber(result?.score);
		} else if (mode === "weight") {
			value =
				toFiniteNumber(result?.raw_weight) ??
				toFiniteNumber(result?.weight_kg) ??
				toFiniteNumber(result?.value) ??
				toFiniteNumber(result?.score);
			estimated1rm = toFiniteNumber(result?.estimated_1rm);
			reps = toFiniteNumber(result?.raw_reps) ?? toFiniteNumber(result?.reps);
		} else if (mode === "time") {
			value =
				toFiniteNumber(result?.time_seconds) ?? toFiniteNumber(result?.value);
		} else {
			value =
				toFiniteNumber(result?.score) ??
				toFiniteNumber(result?.value) ??
				firstNumericSchemaValue(result) ??
				sumIntervalScores(result);
		}

		if (value === null) continue;
		points.push({
			date: dateLabel,
			shortDate: dateLabel ? dateLabel.slice(5) : "",
			value,
			estimated1rm,
			reps,
		});
	}

	if (points.length < 2) return null;

	// Newest-first in, chronological out, capped so the x-axis stays readable.
	const chronological = points.slice(0, 20).reverse();
	const hasEstimated1rm =
		mode === "weight" &&
		chronological.some((point) => point.estimated1rm !== null);

	// Nel tiro la direzione **non si assume**: col time-plus e nel dry fire
	// vince il tempo più basso, con l'hit factor il più alto. È congelata sul
	// risultato (`direction`) e la si legge da lì, perché un grafico ordinato
	// al contrario non sbaglia in modo visibile.
	const shootingSample = isShooting ? list[0]?.shooting || {} : null;
	const shootingUnit = shootingSample
		? shootingSample.metric === "time_plus" ||
			shootingSample.mode === "dry_fire"
			? "seconds"
			: ""
		: "";

	return {
		points: chronological,
		mode,
		unit:
			mode === "weight"
				? "kg"
				: mode === "time"
					? "seconds"
					: mode === "shooting"
						? shootingUnit
						: "",
		hasEstimated1rm,
		lowerIsBetter:
			mode === "time" ||
			(mode === "shooting" && shootingSample?.direction === "lower"),
	};
}
